import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";

export default function TrialsPage() {
  const { hasPermission } = useAuth();
  const [rows, setRows] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "Trial", durationDays: 14 });

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows((await api.get("/v1/trials")).data.data); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    api.get("/v1/tenants").then((r) => setTenants(r.data.data.items)).catch(() => {});
    api.get("/v1/plans").then((r) => setPlans(r.data.data)).catch(() => {});
  }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/v1/trials", { ...form, durationDays: Number(form.durationDays) });
      toast.success("Trial created"); setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const convert = async (t) => {
    const planCode = window.prompt("Convert to plan code (e.g. GOLD):", "GOLD");
    if (!planCode) return;
    try { await api.post(`/v1/trials/${t.id}/convert`, { planCode }); toast.success("Converted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };
  const extend = async (t) => {
    try { await api.patch(`/v1/trials/${t.id}/extend?days=7`); toast.success("Extended 7 days"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="trials-page">
      <PageHeader title="Trial Management" subtitle="Demo, POC, trial & sandbox tenants with conversion"
        actions={hasPermission("TRIAL.CREATE") && (
          <button onClick={() => setOpen(true)} className="inline-flex items-center gap-2 bg-primary text-white rounded-lg px-4 py-2 text-sm font-semibold" data-testid="create-trial-btn">
            <Plus className="h-4 w-4" /> New Trial
          </button>
        )} />
      <DataTable testid="trials-table" rows={rows} loading={loading}
        columns={[
          { key: "type", header: "Type", render: (t) => <Badge color="blue">{t.type}</Badge> },
          { key: "durationDays", header: "Duration", render: (t) => `${t.durationDays} days` },
          { key: "expiryDate", header: "Expiry", render: (t) => (t.expiryDate || "").slice(0, 10) },
          { key: "status", header: "Status", render: (t) => <Badge color={t.status === "Active" ? "green" : t.status === "Converted" ? "teal" : "slate"}>{t.status}</Badge> },
          { key: "_a", header: "", render: (t) => t.status === "Active" && (
            <div className="flex justify-end gap-2">
              {hasPermission("TRIAL.EXTEND") && <button onClick={() => extend(t)} className="text-xs px-2 py-1 rounded border border-slate-300" data-testid={`extend-${t.id}`}>Extend</button>}
              {hasPermission("TRIAL.CONVERT") && <button onClick={() => convert(t)} className="text-xs px-2 py-1 rounded bg-primary text-white" data-testid={`convert-${t.id}`}>Convert</button>}
            </div>
          ) },
        ]} />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="trial-dialog">
            <h3 className="text-lg font-bold text-slate-900 mb-4">Create Trial</h3>
            <label className="block text-sm font-medium text-slate-700 mb-1">Tenant (optional)</label>
            <select value={form.tenantId || ""} onChange={(e) => setForm({ ...form, tenantId: e.target.value || null })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-3" data-testid="trial-tenant">
              <option value="">None</option>
              {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName}</option>)}
            </select>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-3" data-testid="trial-type">
              {["Trial", "Demo", "POC", "Sandbox"].map((t) => <option key={t}>{t}</option>)}
            </select>
            <label className="block text-sm font-medium text-slate-700 mb-1">Duration (days)</label>
            <input type="number" value={form.durationDays} onChange={(e) => setForm({ ...form, durationDays: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm mb-4" data-testid="trial-duration" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-slate-300">Cancel</button>
              <button onClick={create} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-white font-semibold flex items-center gap-2" data-testid="trial-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
